import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from 'framer-motion';
import blogsData from "../../data/blogsData";
import { Helmet } from "react-helmet";
import clinicSpotIcon from "../../assets/clinicspot.png";
import { Link } from "react-router-dom";
import practoIcon from "../../assets/social/practo.webp";
import lybrateIcon from "../../assets/social/lybrate.webp";
import aboutmeIcon from "../../assets/social/about.webp";
import medinizIcon from "../../assets/social/medzin.webp";




import {
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaLinkedin,
  FaClock,
  FaCalendar,
  FaCheckCircle,
  FaQuoteLeft,
  FaAward,
  FaPinterest,
  FaTwitter
} from 'react-icons/fa';
import './BlogPage.scss';

const demoAuthor = {
  image: '/images/blogs/priority.jpeg',
  name: 'Dr. Shankhadeep Saha',
  title: 'Periodontics & Implantology Specialist',
  bio: 'Dr. Shankhadeep Saha is an MDS Periodontics & Implantology specialist with over 9 years of experience in advanced gum treatments, dental implants, and cosmetic dentistry in HSR Layout.',
  specialties: ['Root Canal Therapy', 'Dental Implants', 'Cosmetic Dentistry'],

  socials: [
    {
      label: "LinkedIn",
      url: "https://www.linkedin.com/in/dr-shankhadeep-saha-a363b9111/",
      icon: FaLinkedin,   // ✔ Use icon
      img: null
    },
    {
      label: "Instagram",
      url: "https://www.instagram.com/dr_shankha/",
      icon: FaInstagram,  // ✔ Use icon
      img: null
    },
    {
      label: "Practo",
      url: "https://www.practo.com/bangalore/doctor/shankhadeep-saha-dentist",
      icon: null,
      img: practoIcon     // ✔ Use image
    },
    {
      label: "Lybrate",
      url: "https://www.lybrate.com/bangalore/doctor/dr-shankhadeep-saha-dentist",
      icon: null,
      img: lybrateIcon
    },
    {
      label: "AboutMe",
      url: "https://about.me/dr.shankhadeep_saha",
      icon: null,
      img: aboutmeIcon
    },
    {
      label: "Mediniz",
      url: "https://mediniz.com/public/feeds/Dental-implant-and-gum-care-services-by-Dr-Shankhadeep-Saha-HSR-Layout",
      icon: null,
      img: medinizIcon
    },
  ]
};


export default function BlogPage() {
  const { slug } = useParams();
  const blog = blogsData.find(b => b.slug === slug);
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(null);

  // Create refs for each section
  const sectionRefs = useRef({});

  const { scrollYProgress } = useScroll();
  const headerY = useTransform(scrollYProgress, [0, 0.3], [0, -100]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // Get recent posts dynamically (exclude current blog)
  const recentPosts = useMemo(() => {
    return blogsData
      .filter(b => b.slug !== slug)
      .slice(0, 3)
      .map(b => ({
        image: b.image,
        title: b.title,
        date: new Date(b.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        }),

        slug: b.slug,
        category: 'Dental Care'
      }));
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrolled / docHeight) * 100;
      setReadingProgress(progress);

      // Detect active section
      Object.keys(sectionRefs.current).forEach((key) => {
        const section = sectionRefs.current[key];
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
            setActiveSection(key);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sectionRefs]);

  // Function to scroll to section
  const scrollToSection = (index) => {
    const section = sectionRefs.current[index];
    if (section) {
      const offset = 100;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!blog) return <div className="blog-error">Blog Not Found</div>;

  return (
    <div className="premium-blog-wrapper">

      <Helmet>
        <title>{blog.title}</title>

        <meta name="description" content={blog.shortDescription} />

        {/* Canonical URL */}
        {blog.seo?.canonicalUrl && (
          <link rel="canonical" href={blog.seo.canonicalUrl} />
        )}

        {/* OG Tags for social sharing */}
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.shortDescription} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:url" content={blog.seo?.url} />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.shortDescription} />
        <meta name="twitter:image" content={blog.image} />
        <meta name="twitter:card" content="summary_large_image" />

      </Helmet>


      {/* Reading Progress Bar */}
      <motion.div
        className="reading-progress-bar"
        style={{ width: `${readingProgress}%` }}
      />





      {/* Luxury Hero Section */}
      <motion.section
        className="luxury-hero"
        style={{ y: headerY, opacity: headerOpacity }}
      >
        <div className="hero-background">
          <motion.img
            src={blog.image}
            alt={blog.title}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
          <div className="hero-gradient-overlay"></div>
          <div className="hero-particles"></div>
        </div>

        <motion.div
          className="hero-content-luxury"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >


          <h1 className="hero-title-luxury">{blog.title}</h1>

          <div className="hero-meta-luxury">
            <span><FaClock /> {blog.readTime}</span>
            <span className="divider">•</span>
            <span>
              <FaCalendar />
              {new Date(blog.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric"
              })}
            </span>


          </div>

          {blog.shortDescription && (
            <motion.p
              className="hero-description"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              {blog.shortDescription}
            </motion.p>
          )}
        </motion.div>


      </motion.section>

      {/* Main Content */}
      <div className="luxury-content-wrapper">
        <div className="content-container">
          <div className="content-grid-luxury">
            {/* Main Article */}
            <motion.main
              className="main-article-luxury"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Content Blocks */}
              {blog.blocks.map((block, idx) => (
                <div
                  key={idx}
                  ref={(el) => (sectionRefs.current[idx] = el)}
                  id={`section-${idx}`}
                >
                  <LuxuryBlock block={block} index={idx} />
                </div>
              ))}

            </motion.main>

            {/* Premium Sidebar */}
            <motion.aside
              className="sidebar-luxury"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <TableOfContents
                blocks={blog.blocks}
                scrollToSection={scrollToSection}
                activeSection={activeSection}
              />

              <RecentPostsLuxury posts={recentPosts} />
              <SocialMediaLuxury />
              <SidebarCTA />
            </motion.aside>
          </div>
        </div>
      </div>

      {/* Premium Author Section */}
      <AuthorSectionLuxury author={demoAuthor} />

      {/* Related Articles */}
      <RelatedArticles posts={recentPosts} />
    </div>
  );
}

// Luxury Content Block
function LuxuryBlock({ block, index }) {

  // 👉 STEP 1: Add this function here
  const convertLinks = (text) => {
    if (!text) return text;

    // Make a copy
    let updated = text;

    // Paragraph keyword linking
    updated = updated.replace(
      /Dental Clinic in HSR/gi,
      `<a href="https://www.kosmixdentalclinic.com" target="_blank" class="inline-link">Dental Clinic in HSR</a>`
    );

    // Title linking
    updated = updated.replace(
      /Dental Implants in HSR/gi,
      `<a href="https://www.kosmixdentalclinic.com/services/dental-implants-in-hsr" target="_blank" class="inline-link">
       Dental Implants in HSR
    </a>`
    );

    return updated;
  };


  const variants = {
    hidden: { opacity: 0, y: 50, rotateX: -15 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  switch (block.type) {
    case 'intro':
      return (
        <motion.section
          className="luxury-block intro-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="block-header-luxury">
            <div className="header-line"></div>
            <h2>{block.title}</h2>
            <div className="header-ornament">✦</div>
          </div>
          <ul className="luxury-list">
            {block.paragraphs?.map((p, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ x: 10, backgroundColor: '#fff7ed' }}
              >
                <span className="check-icon"><FaCheckCircle /></span>
                <span className="list-text">{p}</span>
              </motion.li>
            ))}
          </ul>
        </motion.section>
      );

    case "table":
      return (
        <motion.section
          className="luxury-block table-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="block-header-luxury">
            <div className="header-line"></div>
            <h2>{block.title}</h2>
            <div className="header-ornament">✦</div>
          </div>

          {/* FIXED TABLE CONTAINER */}
          <div className="table-container-luxury">
            <table className="luxury-table auto-fit-table">
              <thead>
                <tr>
                  {block.columns?.map((col, i) => (
                    <motion.th
                      key={i}
                      initial={{ opacity: 0, y: -20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      {col}
                    </motion.th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {block.rows?.map((row, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: i * 0.05 }}
                    viewport={{ once: true }}
                    whileHover={{ backgroundColor: "#fff7ed", scale: 1.01 }}
                  >
                    {row.map((cell, j) => (
                      <td key={j}>{cell}</td>
                    ))}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.section>
      );



    case 'bullet-section':
      return (
        <motion.section
          className="luxury-block bullet-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h3
            className="subheading-luxury"
            dangerouslySetInnerHTML={{ __html: convertLinks(block.title) }}
          />
          <div className="bullet-grid">
            {block.bullets?.map((bullet, i) => (
              <motion.div
                key={i}
                className="bullet-card"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(86,27,15,0.2)' }}
              >
                <div className="bullet-icon">✓</div>
                <p>{bullet}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      );

    case 'tips':
      return (
        <motion.section
          className="luxury-block tips-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="tips-header-luxury">
            <FaAward className="tips-icon" />
            <h3>{block.title}</h3>
          </div>
          <div className="tips-grid">
            {block.items?.map((tip, i) => (
              <motion.div
                key={i}
                className="tip-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{
                  y: -10,

                }}
              >
                <div className="tip-number">{i + 1}</div>
                <p>{tip}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      );

    case 'faq-section':
      return (
        <motion.section
          className="luxury-block faq-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="block-header-luxury">
            <div className="header-line"></div>
            <h2>{block.title}</h2>
            <div className="header-ornament">✦</div>
          </div>
          <div className="faq-accordion">
            {block.faqs?.map((faq, i) => (
              <FAQItem key={i} faq={faq} index={i} />
            ))}
          </div>
        </motion.section>
      );

    case 'quote-section':
      return (
        <motion.section
          className="luxury-block quote-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h3 className="subheading-luxury">{block.title}</h3>
          {block.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
          <motion.blockquote
            className="luxury-quote"
            whileHover={{ scale: 1.02 }}
          >
            <FaQuoteLeft className="quote-icon-luxury" />
            <p>{block.quote}</p>
            <div className="quote-line"></div>
          </motion.blockquote>
        </motion.section>
      );

    case 'clinic-highlight':
      return (
        <motion.section
          className="luxury-block clinic-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="clinic-badge">Premium Care</div>
          <h3 className="clinic-title">{block.title}</h3>
          {block.paragraphs?.map((p, i) => (
            <p
              key={i}
              dangerouslySetInnerHTML={{ __html: convertLinks(p) }}
            />
          ))}

          <ul className="luxury-list">
            {block.bullets?.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <FaCheckCircle />
                <span>{b}</span>
              </motion.li>
            ))}
          </ul>
          {block.cta && (
            <motion.a
              href={block.cta.url}
              className="cta-button-luxury"
              whileHover={{ scale: 1.05, boxShadow: '0 15px 30px rgba(198,156,109,0.4)' }}
              whileTap={{ scale: 0.95 }}
            >
              <span>{block.cta.label}</span>
              <div className="button-shimmer"></div>
            </motion.a>
          )}
        </motion.section>
      );

    case 'conclusion':
      return (
        <motion.section
          className="luxury-block conclusion-luxury"
          variants={variants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{ textAlign: "left" }}   // LEFT ALIGN
        >
          <div className="conclusion-ornament">Conclusion</div>

          {/* SHOW ONLY FIRST TWO PARAGRAPHS */}
          {block.paragraphs?.slice(0, 2).map((p, i) => (
            <p key={i}>{p}</p>
          ))}

        </motion.section>
      );


    default:
      return null;
  }
}

// FAQ Item with Animation
function FAQItem({ faq, index }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className={`faq-item-luxury ${isOpen ? 'open' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <motion.div
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ x: 5 }}
      >
        <span>{faq.question}</span>
        <motion.div
          className="faq-icon"
          animate={{ rotate: isOpen ? 45 : 0 }}
        >
          +
        </motion.div>
      </motion.div>
      <motion.div
        className="faq-answer"
        initial={{ height: 0, opacity: 0 }}
        animate={{
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
      >
        <p>{faq.answer}</p>
      </motion.div>
    </motion.div>
  );
}



// Table of Contents with Click Functionality
function TableOfContents({ blocks, scrollToSection, activeSection }) {
  return (
    <motion.div
      className="toc-widget"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <h3>Table of Contents</h3>
      <ul>
        {blocks.slice(0, 6).map((block, i) => (
          <motion.li
            key={i}
            className={activeSection === i ? 'active' : ''}
            onClick={() => scrollToSection(i)}
            whileHover={{ x: 10, color: '#22d3ee' }}
            whileTap={{ scale: 0.98 }}
            style={{ cursor: 'pointer' }}
          >
            <span className="toc-number">{i + 1}.</span>
            <span className="toc-title">{block.title}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
}



// Recent Posts Luxury
function RecentPostsLuxury({ posts }) {
  if (!posts || posts.length === 0) return null;

  return (
    <div className="widget-luxury recent-posts-luxury">
      <h3 className="widget-title-luxury">Trending Articles</h3>

      {posts.map((post, i) => (
        <motion.div
          key={i}
          className="post-card-luxury"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
          viewport={{ once: true }}
          whileHover={{ x: -5, boxShadow: "0 15px 30px rgba(86,27,15,0.15)" }}
        >
          <Link to={`/blog/${post.slug}`} className="post-link-luxury">
            <div className="post-image-luxury">
              <img src={post.image} alt={post.title} />
            </div>

            <div className="post-content-luxury">
              <span className="post-date-luxury">{post.date}</span>
              <h4>{post.title}</h4>
            </div>
          </Link>
        </motion.div>
      ))}
    </div>
  );
}

// Social Media Luxury
function SocialMediaLuxury() {
  const socials = [
    { icon: <FaFacebookF />, label: "Facebook", color: "#1877f2", url: "https://www.facebook.com/kosmixhsr" },

    { icon: <FaInstagram />, label: "Instagram", color: "#e4405f", url: "https://www.instagram.com/kosmixhsr/" },

    { icon: <FaYoutube />, label: "YouTube", color: "#ff0000", url: "https://www.youtube.com/@kosmixhsr" },

    { icon: <FaTwitter />, label: "Twitter", color: "#0077b5", url: "https://x.com/kosmixhsr" },

    { icon: <FaPinterest />, label: "Pinterest", color: "#bd081c", url: "https://in.pinterest.com/kosmixhsr/" },

    // ⭐ ClinicSpots
    { img: clinicSpotIcon, label: "ClinicSpots", color: "#5b1b1b", url: "https://www.clinicspots.com/dentist/bangalore/hsr-layout" },
  ];



  return (
    <div className="widget-luxury social-luxury">
      <h3 className="widget-title-luxury">Connect With Us</h3>

      <div className="social-buttons-luxury">
        {socials.map((social, i) => (
          <motion.a
            key={i}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            className="social-btn-luxury"
            whileHover={{
              scale: 1.15,
              backgroundColor: social.color,
              rotate: [0, -10, 10, 0],
            }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            {social.icon ? (
              social.icon
            ) : (
              <img
                src={social.img}
                alt={social.label}
                className="custom-social-icon"
              />
            )}
          </motion.a>
        ))}
      </div>
    </div>
  );

}

function SidebarCTA() {
  return (
    <motion.div
      className="sidebar-cta-widget"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <h3>Need Expert Dental Care?</h3>

      <p>
        Book your appointment today and experience gentle, advanced, and
        personalized dental treatment from our specialists.
      </p>

      <motion.a
        href="/#contact"
        className="cta-button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Book Appointment →
      </motion.a>
    </motion.div>
  );
}





// Author Section Luxury
function AuthorSectionLuxury({ author }) {
  return (
    <motion.section
      className="author-section-luxury"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <div className="author-container-luxury">
        <motion.div
          className="author-image-luxury"
          whileHover={{ scale: 1.05, rotate: 5 }}
        >
          <img src={author.image} alt={author.name} />
          <div className="author-badge-luxury">
            <FaAward /> Expert
          </div>
        </motion.div>
        <div className="author-info-luxury">
          <h3>{author.name}</h3>
          <span className="author-role-luxury">{author.title}</span>
          <p>{author.bio}</p>
          <div className="author-specialties">
            {author.specialties.map((spec, i) => (
              <motion.span
                key={i}
                className="specialty-tag"
                whileHover={{ scale: 1.1, backgroundColor: '#22d3ee' }}
              >
                {spec}
              </motion.span>
            ))}

            {/* Social Icons */}
            {author.socials && (
              <div className="author-socials">
                {author.socials.map((s, i) => (
                  <motion.a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="author-social-icon"
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {s.icon ? (
                      <s.icon size={35} color="#854d0e" />
                    ) : (
                      <img src={s.img} alt={s.label} className="author-social-img" />
                    )}
                  </motion.a>
                ))}
              </div>
            )}


          </div>
        </div>
      </div>
    </motion.section>
  );
}

// Related Articles
function RelatedArticles({ posts }) {
  if (!posts || posts.length === 0) return null;

  return (
    <section className="related-articles-luxury">
      <div className="section-header-luxury">
        <h2>Continue Reading</h2>
        <p>Explore more expert insights</p>
      </div>

      <div className="related-grid">
        {posts.map((post, i) => (
          <motion.div
            key={i}
            className="related-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.2 }}
            viewport={{ once: true }}
            whileHover={{ y: -10, boxShadow: "0 30px 60px rgba(86,27,15,0.2)" }}
          >
            <Link to={`/blog/${post.slug}`} className="related-link">
              <div className="related-image">
                <img src={post.image} alt={post.title} />
              </div>

              <div className="related-content">
                <span className="related-date">{post.date}</span>

                <h4>{post.title}</h4>

                <span className="read-more-luxury">Read Article →</span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
